// src/components/dashboard/BookDetailsModal.jsx

import { X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import config from "../../config";

export default function BookDetailsModal({ isOpen, onClose, book }) {
  if (!book) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Get the first image URL from bookImages array
  const bookImageUrl = book.bookImages && book.bookImages.length > 0 
    ? `${config.url}${book.bookImages[0]}`
    : null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={handleBackdropClick}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2 }}
            className="relative bg-white dark:bg-gray-900 rounded-xl shadow-2xl dark:shadow-black/40 border border-gray-200 dark:border-gray-800 max-w-2xl w-full mx-4 overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-800">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Book Details
              </h3>
              <button
                onClick={onClose}
                className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-150"
                aria-label="Close modal"
              >
                <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              <div className="flex gap-6">
                {/* Book Image - Larger size for better visibility */}
                <div className="flex-shrink-0">
                  <div className="w-48 h-64 rounded-lg overflow-hidden shadow-lg border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800">
                    {bookImageUrl ? (
                      <img
                        src={bookImageUrl}
                        alt={book.bookName}
                        className="w-full h-full object-fit"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center"><span class="text-gray-400 dark:text-gray-600 text-sm">No Image</span></div>';
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <span className="text-gray-400 dark:text-gray-600 text-sm">
                          No Image
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Book Information */}
                <div className="flex-1 space-y-4">
                  {/* Book Name */}
                  {book.bookName && (
                    <div>
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                        Book Name
                      </label>
                      <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                        {book.bookName}
                      </p>
                    </div>
                  )}

                  {/* Author */}
                  {book.author?.authorName && (
                    <div>
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                        Author
                      </label>
                      <p className="text-base text-gray-700 dark:text-gray-300">
                        {book.author.authorName}
                      </p>
                    </div>
                  )}

                  {/* Language */}
                  {book.bookLanguage && (
                    <div>
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                        Language
                      </label>
                      <p className="text-base text-gray-700 dark:text-gray-300">
                        {book.bookLanguage}
                      </p>
                    </div>
                  )}

                  {/* Category */}
                  {book.category?.categoryName && (
                    <div>
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                        Category
                      </label>
                      <p className="text-base text-gray-700 dark:text-gray-300">
                        {book.category.categoryName}
                      </p>
                    </div>
                  )}

                  {/* ISBN */}
                  {(book.bookIsbn10 || book.bookIsbn13) && (
                    <div>
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                        ISBN
                      </label>
                      <p className="text-sm text-gray-600 dark:text-gray-400 font-mono">
                        {book.bookIsbn13 || book.bookIsbn10}
                      </p>
                    </div>
                  )}

                  {/* Publication */}
                  {book.bookPublication && (
                    <div>
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                        Publication
                      </label>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {book.bookPublication}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
